/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion } from 'motion/react';
import React, { useMemo, useState, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Briefcase, Users, Calendar, ChevronDown, Filter, LayoutList, Award, BookOpen, User, ShieldCheck, ShieldAlert, UserCheck, TrendingUp, MapPin, Sparkles, Building2, ChevronRight, Mail, Loader2, CheckCircle, X, Cake } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { db, isUsingMockKeys } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import dashboardHeroBg from '../../assets/images/dashboard_hero_bg_1780864511918.png';
import { HeroBanner } from '../Common/HeroBanner';
import { StudentAvatar } from '../Common/StudentAvatar';
import { FerpaConsentReminderCard } from '../Common/FerpaConsentReminderCard';
import { Ferpa18TransitionModal } from '../Common/Ferpa18TransitionModal';
import { useInAppDialog } from '../Common/InAppDialog';
import { calculateAge } from '../../utils/timeUtils';

interface StudentDashboardProps {
    overrideUser?: any;
    isReadOnly?: boolean;
    familySwitcher?: React.ReactNode;
}

export const StudentDashboard: React.FC<StudentDashboardProps> = ({ overrideUser, isReadOnly, familySwitcher }) => {
    const {
        activeUser: contextUser,
        activeTenant,
        applications,
        opportunities,
        tasks,
        placements,
        interviews,
        users,
        setCurrentView,
        viewingStudentId,
        selectedTerm,
        sendVerificationReminderToAdvisor,
        process18thBirthdayTransition
    } = useWBL();

    const { showAlert, showToast } = useInAppDialog();
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderSent, setReminderSent] = useState(false);
    const [showFerpaReminderModal, setShowFerpaReminderModal] = useState(false);
    const [show18Modal, setShow18Modal] = useState(false);

    const handleSendAdvisorReminder = async () => {
        setSendingReminder(true);
        try {
            await sendVerificationReminderToAdvisor();
            setReminderSent(true);
            showToast({
                title: "Reminder Sent",
                message: "Verification reminder sent to your school advisor!",
                type: "success"
            });
        } catch (err: any) {
            showAlert({
                title: "Failed to Send Reminder",
                message: "Unable to send verification reminder: " + (err?.message || "Please retry later."),
                type: "error"
            });
        } finally {
            setSendingReminder(false);
        }
    };

    const activeUser = overrideUser || contextUser;
    const studentUid = viewingStudentId || activeUser?.uid;

    const [cardTab, setCardTab] = useState<'outlook' | 'education'>('outlook');

    const sectorStats = useMemo(() => {
        // Get open opportunities matching active tenant/region
        const localOpps = (opportunities || []).filter(o => o.status === 'open');
        const total = localOpps.length;
        if (total === 0) {
            return { total: 0, sectors: [], isSystemWide: false };
        }

        const counts: Record<string, number> = {};
        localOpps.forEach(opp => {
            const cluster = opp.careerCluster || opp.industry || 'General Technical';
            counts[cluster] = (counts[cluster] || 0) + 1;
        });

        const colors = ['#0094c8', '#10b981', '#f59e0b', '#8b5cf6', '#3b82f6', '#ec4899', '#f43f5e', '#84cc16'];
        const sectors = Object.entries(counts)
            .map(([name, count], index) => {
                const percentage = Math.round((count / total) * 100);
                const demand = percentage > 30 ? 'High Demand' : percentage > 15 ? 'Growing' : 'Stable';
                return {
                    name,
                    count,
                    percentage,
                    color: colors[index % colors.length],
                    demand
                };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 4);

        return {
            total,
            sectors,
            isSystemWide: false
        };
    }, [opportunities]);




    const [districts, setDistricts] = useState<{id: string, name: string}[]>([]);
    const [schools, setSchools] = useState<{id: string, name: string}[]>([]);
    const [foundations, setFoundations] = useState<any[]>([]);
    const [foundationProgress, setFoundationProgress] = useState<any[]>([]);

    useEffect(() => {
        if (isUsingMockKeys) return;
        const unsubD = onSnapshot(collection(db, 'districts'), (snap) => {
            setDistricts(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
        });
        const unsubS = onSnapshot(collection(db, 'schools'), (snap) => {
            setSchools(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
        });
        const unsubF = onSnapshot(collection(db, 'foundations'), (snap) => {
            setFoundations(snap.docs.map(f => ({ id: f.id, ...(f.data() as any) })));
        });
        const unsubP = onSnapshot(collection(db, 'foundation_progress'), (snap) => {
            setFoundationProgress(snap.docs.map(p => ({ id: p.id, ...(p.data() as any) })));
        });
        return () => {
            unsubD();
            unsubS();
            unsubF();
            unsubP();
        };
    }, []);

    const [showMissingItems, setShowMissingItems] = useState(false);

    const { readiness, missingItems } = useMemo(() => {
        if (!activeUser) return { readiness: 0, missingItems: [] };
        
        const criteria = [
            { label: 'Profile Picture', value: !!activeUser.avatar },
            { label: 'First Name', value: !!activeUser.contactFirstName },
            { label: 'Last Name', value: !!activeUser.contactLastName },
            { label: 'Date of Birth', value: !!activeUser.dateOfBirth },
            { label: 'Email Address', value: !!(activeUser.email || activeUser.contactEmail) },
            { label: 'Phone Number', value: !!activeUser.contactPhone },
            { label: 'FERPA Consent', value: (typeof activeUser.ferpaConsent === 'object' ? !!activeUser.ferpaConsent?.granted : !!activeUser.ferpaConsent) },
            { label: 'Academic Level', value: !!activeUser.academicLevel },
            { label: 'Graduation Year', value: !!activeUser.classYear },
            { label: 'School District', value: !!activeUser.district },
            { label: 'High School', value: !!activeUser.school },
            { label: 'Interests', value: !!activeUser.targetingMap && activeUser.targetingMap.length > 0 },
            { label: 'Professional Summary', value: !!activeUser.description },
            { label: 'Resume', value: !!activeUser.resumeUrl }
        ];

        const filled = criteria.filter(c => c.value).length;
        const missing = criteria.filter(c => !c.value).map(c => c.label);
        
        return {
            readiness: Math.floor((filled / criteria.length) * 100),
            missingItems: missing
        };
    }, [activeUser]);

    const myApplications = useMemo(() => 
        applications.filter(a => {
            const opp = (opportunities || []).find(o => o.id === a.opportunityId);
            const termMatch = opp
                ? (opp.semester === selectedTerm || opp.term === selectedTerm || (!opp.semester && !opp.term))
                : (a.term === selectedTerm || !a.term);
            return a.studentId === studentUid && termMatch;
        }),
        [applications, studentUid, selectedTerm, opportunities]
    );

    const appliedCount = myApplications.length;
    const approvedCount = myApplications.filter(a => ['accepted', 'approved', 'placed'].includes(a.status)).length;
    const declinedCount = myApplications.filter(a => ['rejected', 'declined', 'withdrawn'].includes(a.status)).length;
    const interviewedCount = myApplications.filter(a => ['reviewed', 'interviewing', 'scheduled'].includes(a.status) || a.interviewScheduled || interviews.some(i => i.applicationId === a.id)).length;

    const myTasks = useMemo(() => 
        tasks.filter(t => t.studentId === studentUid && (t.term === selectedTerm || !t.term)),
        [tasks, studentUid, selectedTerm]
    );

    const masteredTasks = myTasks.filter(t => t.status === 'approved').length;

    const myPlacements = useMemo(() => 
        placements.filter(p => {
            if (p.studentId !== studentUid) return false;
            const opp = (opportunities || []).find(o => o.id === p.opportunityId);
            const placementTerm = p.term || opp?.semester || opp?.term;
            return !selectedTerm || placementTerm === selectedTerm || !placementTerm;
        }),
        [placements, studentUid, selectedTerm, opportunities]
    );

    const myInterviews = useMemo(() => 
        interviews.filter(i => i.studentId === studentUid && (i.term === selectedTerm || !i.term)),
        [interviews, studentUid, selectedTerm]
    );

    const displayFoundations = useMemo(() => {
        if (foundations.length > 0) return foundations;
        return [
            { id: 'mock-f1', title: 'Workplace Communication', slides: [1, 2, 3, 4] },
            { id: 'mock-f2', title: 'Professional Etiquette', slides: [1, 2, 3, 4, 5] },
            { id: 'mock-f3', title: 'Problem Solving', slides: [1, 2, 3] },
            { id: 'mock-f4', title: 'Collaborative Teamwork', slides: [1, 2, 3, 4] }
        ];
    }, [foundations]);

    const displayProgress = useMemo(() => {
        const studentProgress = foundationProgress.filter(p => p.userId === studentUid);
        if (studentProgress.length > 0) return studentProgress;
        
        // Only return mock progress for seeded demo students in the training/mock database
        const isDemoStudent = studentUid && (
            studentUid === 'student-sam' || 
            studentUid === 'student-alex' || 
            studentUid === 'student-lily' || 
            studentUid === 'student-max' || 
            studentUid === 'student-john' ||
            studentUid.startsWith('student-')
        );
        
        if (isDemoStudent) {
            return [
                { foundationId: 'mock-f1', userId: studentUid, completedAt: '2026-06-25T12:00:00Z', status: 'completed', quizScore: 95 },
                { foundationId: 'mock-f2', userId: studentUid, completedSlides: [1, 2], status: 'in_progress' }
            ];
        }
        
        return [];
    }, [foundationProgress, studentUid]);

    const activeFoundationsCount = displayFoundations.length;
    const completedFoundationsCount = useMemo(() => {
        return displayFoundations.filter(f => {
            const prog = displayProgress.find(p => p.foundationId === f.id);
            return prog?.completedAt || prog?.status === 'completed' || (prog?.quizScore && prog.quizScore >= 90);
        }).length;
    }, [displayFoundations, displayProgress]);

    const pendingFoundationsCount = Math.max(0, activeFoundationsCount - completedFoundationsCount);

    // Chart data for Career Mastery
    const chartData = [
        { name: 'Mastered', value: completedFoundationsCount },
        { name: 'Pending', value: pendingFoundationsCount > 0 ? pendingFoundationsCount : (completedFoundationsCount === 0 ? 4 : 0) }, 
    ];
    const COLORS = ['#0f172a', '#f3eee6'];

    const formatConsentDate = (dateVal: any) => {
        if (!dateVal) return '';
        try {
            const d = (dateVal && typeof dateVal.toDate === 'function') ? dateVal.toDate() : new Date(dateVal);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleDateString();
        } catch (e) {
            return '';
        }
    };

    const age = calculateAge(activeUser?.dateOfBirth);
    const isMinor = age < 18;

    // Automatically check for 18th birthday FERPA transition
    useEffect(() => {
        if (!activeUser || isReadOnly) return;
        const userAge = calculateAge(activeUser.dateOfBirth);
        if (userAge >= 18) {
            // Trigger backend transition if not already marked processed
            if (!activeUser.ferpa18TransitionProcessed && activeUser.uid) {
                process18thBirthdayTransition(activeUser.uid).catch(err => console.warn("18th birthday auto transition note:", err));
            }
            // Auto open modal if parent oversight choice hasn't been set or FERPA self-consent is missing
            if (!activeUser.parentOversightPreferenceSet || !(typeof activeUser.ferpaConsent === 'object' ? activeUser.ferpaConsent?.granted : activeUser.ferpaConsent)) {
                setShow18Modal(true);
            }
        }
    }, [activeUser?.dateOfBirth, activeUser?.ferpa18TransitionProcessed, activeUser?.parentOversightPreferenceSet, activeUser?.uid, isReadOnly]);

    return (
        <div className="space-y-6">
            {/* Hero Header */}
            <HeroBanner 
                title="DASHBOARD"
                subtitle={isReadOnly ? `Role: PARENT | Perspective: ${activeTenant?.name || 'TRAINING ENVIRONMENT'}` : `Role: ${activeUser?.role ? activeUser.role.replace('_', ' ') : 'STUDENT'} | Perspective: ${activeTenant?.name || 'TRAINING ENVIRONMENT'}`}
                pageKey="dashboard"
            />

            <div className="w-full px-4 md:px-8 space-y-6">
                {/* 18th Birthday FERPA Transition Banner for Adult Students */}
                {!isReadOnly && age >= 18 && (
                    <div className="bg-gradient-to-r from-amber-50 via-sky-50 to-emerald-50 border border-amber-200/90 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs">
                        <div className="flex items-center space-x-3.5">
                            <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-2xs shrink-0">
                                <Cake className="w-5 h-5" />
                            </div>
                            <div className="space-y-0.5">
                                <div className="flex items-center space-x-2">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-900 font-mono">
                                        FERPA Statutory Transition • Age 18
                                    </span>
                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[9px] font-extrabold rounded-full uppercase font-mono">
                                        Adult Self-Consent
                                    </span>
                                </div>
                                <p className="text-xs font-bold text-slate-800">
                                    {activeUser?.parentOversightPreferenceSet 
                                        ? (activeUser?.allowParentOversightAt18 !== false 
                                            ? "Parent Dashboard Oversight: ALLOWED (Student Authorized)" 
                                            : "Parent Dashboard Oversight: REVOKED (Self-Managed Student Account)") 
                                        : "Action Required: Complete 18yo Adult Self-Consent & Parent Oversight Preference"}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShow18Modal(true)}
                            className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-xl text-xs font-black uppercase tracking-wider cursor-pointer shadow-sm transition-all shrink-0 flex items-center space-x-1.5"
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>{activeUser?.parentOversightPreferenceSet ? "Review 18yo Settings" : "Sign 18yo FERPA Self-Consent"}</span>
                        </button>
                    </div>
                )}

                {/* Ultra-Compressed Micro Status & Action Strip */}
                {!isReadOnly && (
                    <div className="space-y-2">
                        <div className="bg-white border border-slate-200/90 rounded-xl px-3.5 py-1.5 shadow-3xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            {/* Left: Readiness & Completeness Link */}
                            <div className="flex items-center gap-2 min-w-0">
                                <Award className="w-3.5 h-3.5 text-[#0094c8] shrink-0" />
                                <span className="font-extrabold text-slate-800 uppercase tracking-tight text-[11px] shrink-0">
                                    Readiness: <strong className="text-[#0094c8] font-mono">{readiness}%</strong>
                                </span>
                                
                                <div className="w-16 sm:w-24 bg-slate-100 rounded-full h-1.5 overflow-hidden shrink-0 hidden xs:block">
                                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${readiness}%`, backgroundColor: activeTenant.primaryColor }} />
                                </div>

                                {missingItems.length > 0 && (
                                    <button 
                                        type="button" 
                                        onClick={() => setShowMissingItems(!showMissingItems)}
                                        className="text-[10px] text-slate-400 hover:text-slate-700 underline font-semibold cursor-pointer truncate"
                                    >
                                        ({missingItems.length} missing)
                                    </button>
                                )}

                                <button 
                                    type="button"
                                    onClick={() => setCurrentView('profile')}
                                    className="text-[10px] font-black text-[#0094c8] hover:underline uppercase tracking-wider ml-1 cursor-pointer shrink-0"
                                >
                                    {readiness === 100 ? 'View' : 'Complete'}
                                </button>
                            </div>

                            {/* Right: Inline FERPA & Advisor Status & Quick Remind Actions */}
                            <div className="flex items-center gap-3 font-mono text-[10px] uppercase font-bold text-slate-600 shrink-0">
                                {/* FERPA Status & Trigger */}
                                {(() => {
                                    const isGranted = typeof activeUser?.ferpaConsent === 'object' ? activeUser.ferpaConsent.granted : (activeUser?.ferpaConsent === true);
                                    return (
                                        <div className="flex items-center gap-1.5">
                                            <span className={isGranted ? 'text-emerald-700 font-extrabold' : 'text-amber-800 font-extrabold'}>
                                                FERPA: {isGranted ? 'Granted' : 'Pending'}
                                            </span>
                                            {!isGranted && (
                                                <button 
                                                    type="button"
                                                    onClick={() => setShowFerpaReminderModal(true)}
                                                    className="px-2 py-0.5 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded font-sans text-[9px] font-black tracking-wider cursor-pointer shadow-3xs transition-colors"
                                                >
                                                    Remind Parent
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}

                                <span className="text-slate-200">•</span>

                                {/* Advisor Status & Trigger */}
                                {(() => {
                                    const isVerified = !!(activeUser?.advisorVerified || activeUser?.verified);
                                    return (
                                        <div className="flex items-center gap-1.5">
                                            <span className={isVerified ? 'text-blue-700 font-extrabold' : 'text-slate-600 font-bold'}>
                                                Advisor: {isVerified ? 'Verified' : 'Pending'}
                                            </span>
                                            {!isVerified && (
                                                <button 
                                                    type="button"
                                                    onClick={handleSendAdvisorReminder}
                                                    disabled={sendingReminder}
                                                    className="px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded font-sans text-[9px] font-bold cursor-pointer transition-colors disabled:opacity-50"
                                                >
                                                    {sendingReminder ? 'Sending...' : reminderSent ? 'Sent!' : 'Remind Advisor'}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Collapsible missing items drawer if opened */}
                        {showMissingItems && missingItems.length > 0 && (
                            <motion.div 
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="bg-white border border-slate-200 rounded-xl p-2.5 shadow-3xs flex flex-wrap items-center gap-1.5 text-[10px]"
                            >
                                <span className="font-mono font-bold text-slate-400 uppercase mr-1">Missing:</span>
                                {missingItems.map((item, idx) => (
                                    <span 
                                        key={idx} 
                                        className="px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600 rounded text-[9px] font-bold uppercase"
                                    >
                                        • {item}
                                    </span>
                                ))}
                            </motion.div>
                        )}
                    </div>
                )}

                {/* Dashboard Grid */}
                <div className={`grid grid-cols-1 ${activeTenant.foundationsEnabled !== false ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8`}>
                    {/* Application Pipeline */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-3">
                                    <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Briefcase className="w-5 h-5" /></div>
                                    <div>
                                        <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">Application Pipeline</h3>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Current recruitment status</p>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3.5">
                                <div className="p-3.5 bg-blue-50/20 hover:bg-blue-50/40 border border-blue-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px] group/item">
                                    <span className="text-[8.5px] font-black uppercase text-blue-500 tracking-wider">SUBMISSIONS</span>
                                    <div className="flex justify-between items-baseline mt-2">
                                        <p className="text-2xl font-black text-slate-800 tracking-tight">{appliedCount}</p>
                                    </div>
                                </div>
                                <div className="p-3.5 bg-sky-50/20 hover:bg-sky-50/40 border border-sky-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                                    <span className="text-[8.5px] font-black uppercase text-sky-600 tracking-wider">INTERVIEWING</span>
                                    <div className="flex justify-between items-baseline mt-2">
                                        <p className="text-2xl font-black text-sky-700 tracking-tight">{interviewedCount}</p>
                                    </div>
                                </div>
                                <div className="p-3.5 bg-emerald-50/20 hover:bg-emerald-50/40 border border-emerald-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                                    <span className="text-[8.5px] font-black uppercase text-emerald-600 tracking-wider">ACCEPTED</span>
                                    <div className="flex justify-between items-baseline mt-2">
                                        <p className="text-2xl font-black text-emerald-700 tracking-tight">{approvedCount}</p>
                                    </div>
                                </div>
                                <div className="p-3.5 bg-rose-50/25 hover:bg-rose-50/45 border border-rose-100/50 rounded-xl transition-all duration-300 flex flex-col justify-between min-h-[82px]">
                                    <span className="text-[8.5px] font-black uppercase text-rose-500 tracking-wider">DECLINED</span>
                                    <div className="flex justify-between items-baseline mt-2">
                                        <p className="text-2xl font-black text-rose-600 tracking-tight">{declinedCount}</p>
                                    </div>
                                </div>
                            </div>
                            {/* Consolidated Latest Placement Section */}
                            <div className={`mt-4 p-3 rounded-xl flex items-center justify-between border transition-all duration-300 ${
                                myPlacements.length > 0 
                                    ? 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/20' 
                                    : 'bg-slate-50/50 border-slate-100'
                            }`}>
                                <div className="min-w-0 flex-1 pr-2">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Active Internship</p>
                                    <p className="text-[11px] font-extrabold text-[#0f172a] truncate uppercase">
                                        {myPlacements[0] 
                                            ? `${myPlacements[0].opportunityTitle || 'Internship'} @ ${myPlacements[0].organizationName || 'Employer Worksite'}` 
                                            : 'No Active Placements'}
                                    </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                                    myPlacements.length > 0 ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-slate-100 text-slate-500 border border-slate-200/60'
                                } border`}>
                                    {myPlacements.length > 0 ? 'ACTIVE' : 'SEARCHING'}
                                </span>
                            </div>
                        </div>
                        {!isReadOnly && (
                            <button 
                                onClick={() => setCurrentView('applications')} 
                                style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                                className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto cursor-pointer border"
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                            >
                                VIEW MY PIPELINE
                            </button>
                        )}
                    </div>
 
                    {activeTenant.foundationsEnabled !== false && (
                        /* Career Mastery */
                        <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-3">
                                    <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl"><Award className="w-5 h-5" /></div>
                                    <div>
                                        <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">Career Mastery</h3>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Soft-skill foundation progress</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-4 py-2">
                                <div className="w-[110px] h-[110px] relative shrink-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={chartData} innerRadius={36} outerRadius={48} paddingAngle={4} dataKey="value">
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <p className="text-xl font-black text-slate-800 tracking-tight leading-none">{completedFoundationsCount}</p>
                                        <p className="text-[7.5px] uppercase font-black text-slate-400 tracking-wider mt-0.5">OF {activeFoundationsCount}</p>
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1.5">Mastery Rating</p>
                                    <h4 className="text-base font-black text-[#0f172a] uppercase tracking-tight leading-none mb-1">
                                        {completedFoundationsCount === activeFoundationsCount ? 'Distinguished' : completedFoundationsCount > 0 ? 'Proficient' : 'Beginning'}
                                    </h4>
                                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight leading-normal">
                                        {completedFoundationsCount} of {activeFoundationsCount} soft-skill courses mastered.
                                    </p>
                                </div>
                            </div>

                            {/* List of soft skills modules */}
                            <div className="space-y-2 max-h-[140px] overflow-y-auto pr-1">
                                {displayFoundations.slice(0, 3).map((f) => {
                                    const prog = displayProgress.find(p => p.foundationId === f.id);
                                    const isCompleted = prog?.completedAt || prog?.status === 'completed' || (prog?.quizScore && prog.quizScore >= 90);
                                    const completedSlidesCount = prog?.completedSlides?.length || 0;
                                    const totalSlidesCount = f.slides?.length || 1;
                                    const pct = isCompleted ? 100 : Math.min(100, Math.round((completedSlidesCount / totalSlidesCount) * 100));

                                    return (
                                        <div key={f.id} className="flex items-center justify-between text-xs bg-slate-50/60 hover:bg-slate-50 p-2.5 rounded-xl border border-slate-100 transition-all duration-200">
                                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                                                <div className={`w-2 h-2 rounded-full shrink-0 ${isCompleted ? 'bg-emerald-500' : completedSlidesCount > 0 ? 'bg-amber-500' : 'bg-slate-300'}`} />
                                                <span className="font-extrabold text-[#0f172a] truncate uppercase tracking-tight text-[10px]">{f.title}</span>
                                            </div>
                                            <span className="text-[9px] font-mono font-bold text-slate-500 shrink-0 ml-2">
                                                {isCompleted ? 'MASTERED' : pct > 0 ? `${pct}%` : '0%'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            {!isReadOnly && (
                                <button 
                                    onClick={() => setCurrentView('foundations')} 
                                    style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                                    className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-auto cursor-pointer border"
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                                >
                                    ENTER FOUNDATION HUB
                                </button>
                            )}
                        </div>
                    )}
 
                    {/* My Education & Regional Outlook */}
                    <div className="bg-white p-6 rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(0,0,0,0.01)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:border-slate-300/80 transition-all duration-300 flex flex-col justify-between h-[420px] group">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor }} className="p-2.5 rounded-xl">
                                    {cardTab === 'outlook' ? <TrendingUp className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-sm text-[#0f172a] tracking-tight uppercase">
                                        {cardTab === 'outlook' ? 'Regional Outlook' : 'My Education'}
                                    </h3>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                        {cardTab === 'outlook' ? 'Available jobs & sectors' : 'School and District context'}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Visual Toggle Pill Button */}
                            <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/60 text-[9px] font-black tracking-wider uppercase shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setCardTab('outlook')}
                                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                                        cardTab === 'outlook'
                                            ? 'bg-white text-slate-800 shadow-xs'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                >
                                    Outlook
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCardTab('education')}
                                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                                        cardTab === 'education'
                                            ? 'bg-white text-slate-800 shadow-xs'
                                            : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                >
                                    Edu
                                </button>
                            </div>
                        </div>

                        {cardTab === 'outlook' ? (
                            <div className="flex-1 flex flex-col justify-between min-h-0">
                                <div className="space-y-4">
                                    {/* Brief regional metrics header */}
                                    <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                                        <div className="flex items-center space-x-2">
                                            <span className="relative flex h-2 w-2">
                                                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${sectorStats.isSystemWide ? 'bg-indigo-400' : 'bg-emerald-400'}`}></span>
                                                <span className={`relative inline-flex rounded-full h-2 w-2 ${sectorStats.isSystemWide ? 'bg-indigo-500' : 'bg-emerald-500'}`}></span>
                                            </span>
                                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">
                                                {sectorStats.isSystemWide ? 'Portal-wide WBL Pipeline' : 'Active Placement Pipeline'}
                                            </p>
                                        </div>
                                        <span className="text-xs font-black text-slate-800 uppercase tracking-tight px-2 py-0.5 bg-slate-200/50 rounded-md">
                                            {sectorStats.total} {sectorStats.total === 1 ? 'Role' : 'Roles'}
                                        </span>
                                    </div>

                                    {/* Sectors horizontal bars list */}
                                    {sectorStats.sectors.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center text-center py-10 space-y-2">
                                            <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">No Active Vacancies</span>
                                            <span className="text-slate-300 text-[9px]">Active vacancies will automatically populate this outlook list.</span>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {sectorStats.sectors.map((s, idx) => (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-tight">
                                                        <span className="text-slate-700 truncate max-w-[150px]">{s.name}</span>
                                                        <div className="flex items-center space-x-1.5 shrink-0">
                                                            <span className="text-[9px] font-mono text-slate-400 font-bold">({s.count} {s.count === 1 ? 'job' : 'jobs'})</span>
                                                            <span style={{ color: s.color }} className="font-extrabold">{s.percentage}%</span>
                                                        </div>
                                                    </div>
                                                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                        <div 
                                                            style={{ width: `${s.percentage}%`, backgroundColor: s.color }} 
                                                            className="h-full rounded-full transition-all duration-500" 
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {!isReadOnly && (
                                    <button 
                                        onClick={() => setCurrentView('opportunities')} 
                                        style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                                        className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-4 cursor-pointer border"
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                                    >
                                        <span>EXPLORE OPPORTUNITIES</span>
                                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col justify-between min-h-0">
                                <div className="space-y-4">
                                    <div className="p-4 bg-slate-50/50 border border-slate-100 rounded-xl space-y-3">
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">School District</p>
                                            <p className="text-sm font-black text-[#0f172a] uppercase">
                                                {districts.find(d => d.id === activeUser?.district)?.name || activeUser?.district || 'Not Selected'}
                                            </p>
                                        </div>
                                        <div className="pt-3 border-t border-slate-200/50">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Enrolled School</p>
                                            <p className="text-sm font-black text-slate-600 uppercase">
                                                {schools.find(s => s.id === activeUser?.school)?.name || activeUser?.school || 'Not Selected'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Additional Educational Info to fill and consolidate space */}
                                    <div className="grid grid-cols-2 gap-3.5">
                                        <div className="p-3.5 bg-slate-50/40 border border-slate-100 rounded-xl">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Academic Level</p>
                                            <p className="text-xs font-black text-[#0094c8] uppercase mt-1">
                                                {activeUser?.academicLevel || 'Grade 12'}
                                            </p>
                                        </div>
                                        <div className="p-3.5 bg-slate-50/40 border border-slate-100 rounded-xl">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Graduation Class</p>
                                            <p className="text-xs font-black text-slate-700 uppercase mt-1">
                                                {activeUser?.classYear ? `Class of ${activeUser.classYear}` : 'Class of 2026'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {!isReadOnly && (
                                    <button 
                                        onClick={() => setCurrentView('profile')} 
                                        style={{ backgroundColor: `${activeTenant.primaryColor}14`, color: activeTenant.primaryColor, borderColor: activeTenant.primaryColor }}
                                        className="w-full text-center text-[10px] font-black hover:text-white py-3 hover:shadow-md hover:-translate-y-0.5 rounded-xl transition-all duration-300 flex items-center justify-center space-x-1.5 uppercase tracking-widest mt-4 cursor-pointer border"
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = activeTenant.primaryColor}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${activeTenant.primaryColor}14`}
                                    >
                                        UPDATE PROFILE
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>



            {showFerpaReminderModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
                    <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-lg w-full p-6 relative animate-scale-up space-y-4">
                        <button
                            type="button"
                            onClick={() => setShowFerpaReminderModal(false)}
                            className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="space-y-1">
                            <span className="text-[10px] font-black uppercase tracking-widest text-[#0094c8]">Statutory FERPA Authorization</span>
                            <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">Parent Consent Email Reminder</h3>
                        </div>
                        <FerpaConsentReminderCard
                            compact={false}
                            title="Send Reminder to Parent or Legal Guardian"
                            subtitle="Provide your parent or guardian's email below. They will receive a direct, authenticated link to review and sign your FERPA educational disclosure release."
                            buttonText="Send Parent Reminder"
                            onSuccess={() => {
                                setTimeout(() => setShowFerpaReminderModal(false), 2200);
                            }}
                        />
                    </div>
                </div>
            )}

            {show18Modal && (
                <Ferpa18TransitionModal onClose={() => setShow18Modal(false)} />
            )}

            </div>
        </div>
    );
};